import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_META } from "@/lib/meta";
import { fmtNZ } from "@/lib/datetime";
import { useStatuses } from "@/context/StatusContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Calendar, User, History, Building2, Hash, Network, Paperclip, Upload, Trash2, FileText, Download, X, ChevronLeft, ChevronRight, File, FileSpreadsheet, FileArchive, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import JobTimer from "@/components/JobTimer";
import { ClaimJobDialog } from "@/components/UnassignedJobs";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_ATTACH_BYTES = 15 * 1024 * 1024;
const fmtSize = (b) =>
  b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`;

const isPdf = (a) => (a.content_type || "") === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");

// Distinct icon + color per file type.
const fileIcon = (a) => {
  const ct = (a.content_type || "").toLowerCase();
  const name = (a.filename || "").toLowerCase();
  if (isPdf(a)) return { Icon: FileText, color: "text-red-500" };
  if (ct.includes("word") || /\.(docx?|rtf|odt)$/.test(name)) return { Icon: FileText, color: "text-blue-600" };
  if (ct.includes("sheet") || ct.includes("excel") || ct === "text/csv" || /\.(xlsx?|csv|ods)$/.test(name))
    return { Icon: FileSpreadsheet, color: "text-green-600" };
  if (ct.includes("zip") || ct.includes("compressed") || /\.(zip|rar|7z|tar|gz)$/.test(name))
    return { Icon: FileArchive, color: "text-amber-600" };
  if (ct.startsWith("text/")) return { Icon: FileText, color: "text-zinc-500" };
  return { Icon: File, color: "text-zinc-400" };
};

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { metaFor, orderedKeys, map } = useStatuses();
  const canUpdate = user.role !== "viewer";
  const canSeeTimer = user.role === "admin" || user.role === "superadmin";
  const canManageAttachments = ["superadmin", "admin", "manager", "job_dispatcher"].includes(user.role);
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [manualTime, setManualTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbs, setThumbs] = useState({});
  const [pdfThumbs, setPdfThumbs] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index into imageAttachments, or null
  const [pdfView, setPdfView] = useState(null); // {filename, doc, numPages, blobUrl} or null
  const [pdfPage, setPdfPage] = useState(1);
  const pdfCanvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [claimOpen, setClaimOpen] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/jobs/${id}`)
      .then((r) => {
        setJob(r.data);
        setStatus(r.data.status);
      })
      .catch((err) => {
        toast.error(apiError(err.response?.data?.detail));
        navigate("/jobs");
      });
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const imageAttachments = (job?.attachments || []).filter((a) => (a.content_type || "").startsWith("image/"));
  const attachKey = imageAttachments.map((a) => a.id).join(",");
  const pdfKey = (job?.attachments || []).filter(isPdf).map((a) => a.id).join(",");

  // Fetch image attachments as blobs (auth-aware) so we can show inline thumbnails.
  useEffect(() => {
    if (!attachKey) {
      setThumbs({});
      return;
    }
    let active = true;
    const urls = [];
    (async () => {
      const next = {};
      for (const aid of attachKey.split(",")) {
        try {
          const r = await api.get(`/jobs/${id}/attachments/${aid}`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          next[aid] = url;
          urls.push(url);
        } catch {
          /* ignore individual thumbnail failures */
        }
      }
      if (active) setThumbs(next);
      else urls.forEach(URL.revokeObjectURL);
    })();
    return () => {
      active = false;
      urls.forEach(URL.revokeObjectURL);
    };
  }, [id, attachKey]);

  // Render the first page of each PDF attachment to a small thumbnail image.
  useEffect(() => {
    if (!pdfKey) {
      setPdfThumbs({});
      return;
    }
    let active = true;
    (async () => {
      for (const aid of pdfKey.split(",")) {
        try {
          const r = await api.get(`/jobs/${id}/attachments/${aid}`, { responseType: "arraybuffer" });
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(r.data) }).promise;
          const pg = await pdf.getPage(1);
          const base = pg.getViewport({ scale: 1 });
          const vp = pg.getViewport({ scale: 96 / base.width });
          const canvas = document.createElement("canvas");
          canvas.width = vp.width;
          canvas.height = vp.height;
          await pg.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
          const dataUrl = canvas.toDataURL("image/png");
          pdf.destroy();
          if (!active) return;
          setPdfThumbs((t) => ({ ...t, [aid]: dataUrl }));
        } catch {
          /* fall back to the generic PDF icon */
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [id, pdfKey]);

  useEffect(() => {
    if (lightbox === null) return;
    const n = imageAttachments.length;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight" && n > 1) setLightbox((i) => (i + 1) % n);
      else if (e.key === "ArrowLeft" && n > 1) setLightbox((i) => (i - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, imageAttachments.length]);

  // Reset zoom/pan whenever the viewed image changes or the lightbox closes.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [lightbox]);

  useEffect(() => {
    if (!pdfView) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePdf();
      else if (e.key === "ArrowRight") setPdfPage((p) => Math.min(pdfView.numPages, p + 1));
      else if (e.key === "ArrowLeft") setPdfPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pdfView]);

  // Render the current PDF page to the canvas whenever the doc or page changes.
  useEffect(() => {
    if (!pdfView?.doc || !pdfCanvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await pdfView.doc.getPage(pdfPage);
        const canvas = pdfCanvasRef.current;
        if (!canvas || cancelled) return;
        const containerW = Math.min(900, window.innerWidth - 80);
        const base = page.getViewport({ scale: 1 });
        const scale = containerW / base.width;
        const dpr = window.devicePixelRatio || 1;
        const vp = page.getViewport({ scale });
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = `${vp.width}px`;
        canvas.style.height = `${vp.height}px`;
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
      } catch {
        /* ignore render race on fast page switches */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfView, pdfPage]);

  const requiresNote = !!map[status]?.requires_note;
  // Identify the "In Progress" (timer-start) status by behaviour, not a hardcoded key.
  const startKey = orderedKeys().find((k) => map[k]?.timer_start);
  // Riggers may log a manual time (hours) when starting a job (before it has started).
  const showManualTime = user.role === "rigger" && status === startKey && !job?.started_at;

  const updateStatus = async () => {
    if (requiresNote && !note.trim()) {
      toast.error(`A note is required for "${metaFor(status).label}"`);
      return;
    }
    setSaving(true);
    try {
      const payload = { status, note };
      if (showManualTime && manualTime !== "") payload.manual_time = Number(manualTime);
      const r = await api.patch(`/jobs/${id}/status`, payload);
      setJob(r.data);
      setNote("");
      setManualTime("");
      toast.success("Status updated");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!job)
    return <div className="p-8 text-zinc-400 font-mono text-sm">Loading…</div>;

  const attachments = job.attachments || [];
  const usedBytes = attachments.reduce((s, a) => s + (a.size || 0), 0);

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    doUpload(file);
  };

  const doUpload = async (file) => {
    if (!file) return;
    if (attachments.length >= 2) {
      toast.error("A job can have at most 2 attachments");
      return;
    }
    if (usedBytes + file.size > MAX_ATTACH_BYTES) {
      toast.error("Attachments exceed the 15 MB total limit");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/jobs/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total || file.size || 1))),
      });
      toast.success("Attachment added");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canManageAttachments) return;
    const file = e.dataTransfer?.files?.[0];
    doUpload(file);
  };

  const viewAttachment = async (att) => {
    const isImg = (att.content_type || "").startsWith("image/");
    if (isImg) {
      const idx = imageAttachments.findIndex((a) => a.id === att.id);
      if (!thumbs[att.id]) {
        try {
          const r = await api.get(`/jobs/${id}/attachments/${att.id}`, { responseType: "blob" });
          const url = URL.createObjectURL(r.data);
          setThumbs((t) => ({ ...t, [att.id]: url }));
        } catch (err) {
          toast.error(apiError(err.response?.data?.detail));
          return;
        }
      }
      setLightbox(idx >= 0 ? idx : 0);
      return;
    }
    if (isPdf(att)) {
      try {
        const r = await api.get(`/jobs/${id}/attachments/${att.id}`, { responseType: "arraybuffer" });
        const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const doc = await pdfjsLib.getDocument({ data: new Uint8Array(r.data.slice(0)) }).promise;
        setPdfPage(1);
        setPdfView({ filename: att.filename, doc, numPages: doc.numPages, blobUrl });
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      }
      return;
    }
    try {
      const r = await api.get(`/jobs/${id}/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const closeLightbox = () => setLightbox(null);

  const closePdf = () =>
    setPdfView((p) => {
      if (p?.blobUrl) URL.revokeObjectURL(p.blobUrl);
      if (p?.doc) p.doc.destroy();
      return null;
    });

  const adjustZoom = (delta) =>
    setZoom((z) => {
      const next = Math.min(5, Math.max(1, +(z + delta).toFixed(2)));
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });

  const onImgWheel = (e) => {
    e.preventDefault();
    adjustZoom(e.deltaY < 0 ? 0.25 : -0.25);
  };

  const onImgPointerDown = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onImgPointerMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.px + (e.clientX - dragRef.current.x), y: dragRef.current.py + (e.clientY - dragRef.current.y) });
  };
  const onImgPointerUp = () => {
    dragRef.current = null;
  };

  const deleteAttachment = async (att) => {
    try {
      await api.delete(`/jobs/${id}/attachments/${att.id}`);
      toast.success("Attachment removed");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/jobs")}
        data-testid="back-to-jobs"
        className="flex items-center gap-2 text-sm font-bold text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Back to jobs
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${PRIORITY_META[job.priority].cls}`}>
              {PRIORITY_META[job.priority].label} priority
            </span>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${metaFor(job.status).cls}`}
              data-testid="job-detail-status"
            >
              {metaFor(job.status).label}
            </span>
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display" data-testid="job-detail-title">
            {job.title}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6">
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">
              Description
            </h2>
            <p className="text-zinc-700 text-sm leading-relaxed whitespace-pre-wrap">
              {job.description || "No description provided."}
            </p>
          </div>

          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 grid grid-cols-2 gap-5 text-sm">
            <div className="flex items-start gap-3">
              <User size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Assigned to</div>
                <div className="font-semibold text-zinc-900">{job.assigned_to_name || "Unassigned"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Due date</div>
                <div className="font-semibold text-zinc-900 tabular-nums">{job.due_date || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Location</div>
                <div className="font-semibold text-zinc-900">{job.location || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Network size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Department</div>
                <div className="font-semibold text-zinc-900">{job.department || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Created by</div>
                <div className="font-semibold text-zinc-900">{job.created_by_name || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Building2 size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Customer</div>
                <div className="font-semibold text-zinc-900">{job.customer || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Hash size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">SAP ID</div>
                <div className="font-semibold text-zinc-900 tabular-nums">{job.sap_id || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Hash size={18} className="text-zinc-400 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Bricert ID</div>
                <div className="font-semibold text-zinc-900 tabular-nums">{job.bricert_id || "—"}</div>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div
            className={`bg-white border rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 transition-colors ${
              dragOver && canManageAttachments && attachments.length < 2
                ? "border-blue-500 border-2 border-dashed bg-blue-50/50"
                : "border-zinc-200"
            }`}
            data-testid="job-attachments-card"
            onDragOver={(e) => {
              if (!canManageAttachments || attachments.length >= 2) return;
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            onDrop={onDrop}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 flex items-center gap-2">
                <Paperclip size={14} /> Attachments
                <span className="text-zinc-400 normal-case tracking-normal font-normal">
                  ({attachments.length}/2 · {fmtSize(usedBytes)} of 15 MB)
                </span>
              </h2>
              {canManageAttachments && attachments.length < 2 && (
                <label
                  data-testid="attachment-upload-label"
                  className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
                    uploading ? "bg-zinc-200 text-zinc-400" : "bg-blue-600 text-white hover:bg-blue-500"
                  }`}
                >
                  <Upload size={13} /> {uploading ? "Uploading…" : "Add file"}
                  <input
                    type="file"
                    className="hidden"
                    disabled={uploading}
                    onChange={uploadAttachment}
                    data-testid="attachment-file-input"
                  />
                </label>
              )}
            </div>
            {uploading && (
              <div className="mb-4" data-testid="attachment-upload-progress">
                <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                  <span className="font-semibold">Uploading…</span>
                  <span className="tabular-nums">{uploadProgress}%</span>
                </div>
                <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-[width] duration-150 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            {attachments.length === 0 ? (
              <p className="text-sm text-zinc-400">
                No attachments yet.
                {canManageAttachments && " Drag & drop a file here, or use “Add file”."}
              </p>
            ) : (
              <div className="space-y-2">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    data-testid={`attachment-row-${att.id}`}
                    className="flex items-center gap-3 border border-zinc-200 rounded-md px-3 py-2 hover:bg-zinc-50 transition-colors"
                  >
                    {thumbs[att.id] ? (
                      <img
                        src={thumbs[att.id]}
                        alt={att.filename}
                        onClick={() => viewAttachment(att)}
                        data-testid={`attachment-thumb-${att.id}`}
                        className="w-11 h-11 rounded object-cover border border-zinc-200 shrink-0 cursor-pointer"
                      />
                    ) : isPdf(att) && pdfThumbs[att.id] ? (
                      <img
                        src={pdfThumbs[att.id]}
                        alt={att.filename}
                        onClick={() => viewAttachment(att)}
                        data-testid={`attachment-pdf-thumb-${att.id}`}
                        className="w-11 h-11 rounded object-cover object-top border border-zinc-200 bg-white shrink-0 cursor-pointer"
                      />
                    ) : (
                      (() => {
                        const { Icon, color } = fileIcon(att);
                        return <Icon size={20} className={`${color} shrink-0`} data-testid={`attachment-icon-${att.id}`} />;
                      })()
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{att.filename}</div>
                      <div className="text-xs text-zinc-400">
                        {fmtSize(att.size || 0)}
                        {att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => viewAttachment(att)}
                      data-testid={`attachment-view-${att.id}`}
                      className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-500 shrink-0"
                    >
                      <Download size={14} /> View
                    </button>
                    {canManageAttachments && (
                      <button
                        onClick={() => deleteAttachment(att)}
                        data-testid={`attachment-delete-${att.id}`}
                        className="text-zinc-400 hover:text-red-600 shrink-0"
                        title="Remove attachment"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canManageAttachments && attachments.length === 1 && (
              <p className="text-xs text-zinc-400 mt-3" data-testid="attachment-drop-hint">
                Drag &amp; drop a file here to add one more.
              </p>
            )}
          </div>

          {/* History */}
          {job.history?.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6">
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4 flex items-center gap-2">
                <History size={14} /> Status History
              </h2>
              <div className="space-y-3">
                {[...job.history].reverse().map((h, i) =>
                  h.type === "attachment" ? (
                    <div key={`att-${h.at || i}`} className="flex items-start gap-3 text-sm" data-testid="history-attachment-entry">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full shrink-0 border-zinc-300 bg-zinc-50 text-zinc-600 flex items-center gap-1">
                        <Paperclip size={10} /> {h.action === "removed" ? "Removed" : "Added"}
                      </span>
                      <div>
                        <span className="text-zinc-700">
                          {h.action === "removed" ? "Removed attachment" : "Added attachment"} “{h.filename}”
                        </span>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {h.by} · {fmtNZ(h.at)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={`${h.status}-${h.at || i}`} className="flex items-start gap-3 text-sm">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full shrink-0 ${metaFor(h.status).cls}`}>
                        {metaFor(h.status).label}
                      </span>
                      <div>
                        <span className="text-zinc-700">{h.note || "Status changed"}</span>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {h.by} · {fmtNZ(h.at)}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Status update panel */}
        <div className="lg:col-span-1">
          {canSeeTimer && (
          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 mb-6">
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
              Completion Timer
            </h2>
            <div className="text-2xl">
              <JobTimer job={job} />
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Tracks time from <b>In Progress</b> to <b>Processed for Delivery</b>.
            </p>
          </div>
          )}
          {user.role === "rigger" && !job.assigned_to && (
          <div className="bg-zinc-950 text-white p-6 sticky top-6" data-testid="claim-job-panel">
            <h2 className="font-heading text-xl font-bold tracking-tight mb-2">Unassigned job</h2>
            <p className="text-sm text-zinc-400 mb-4">This job is at one of your locations and nobody has it yet.</p>
            <button onClick={() => setClaimOpen(true)} data-testid="detail-claim-button"
              className="w-full bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5">
              Assign to me
            </button>
            <ClaimJobDialog job={claimOpen ? job : null} onClose={() => setClaimOpen(false)} onClaimed={() => load()} />
          </div>
          )}
          {canUpdate && !(user.role === "rigger" && !job.assigned_to) && (
          <div className="bg-zinc-950 text-white p-6 sticky top-6">
            <h2 className="font-heading text-xl font-bold tracking-tight mb-4">
              Update Status
            </h2>
            <label className="text-xs uppercase tracking-wider font-bold text-zinc-400 block mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="status-update-select"
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-none text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {(() => {
                const visible = orderedKeys().filter(
                  (s) => !(user.role === "rigger" && map[s]?.rigger_hidden && s !== startKey)
                );
                // Ensure the job's current status is always a selectable value so the
                // controlled <select> never falls out of sync with React state (which
                // otherwise silently sends the unchanged status on the first save).
                if (status && !visible.includes(status)) {
                  return [
                    <option key={status} value={status} disabled>
                      {metaFor(status).label} (current)
                    </option>,
                    ...visible.map((s) => (
                      <option key={s} value={s}>
                        {metaFor(s).label}
                      </option>
                    )),
                  ];
                }
                return visible.map((s) => (
                  <option key={s} value={s}>
                    {metaFor(s).label}
                  </option>
                ));
              })()}
            </select>
            {showManualTime && (
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider font-bold text-zinc-400 block mb-2">
                  Manual Time (hrs) <span className="font-normal normal-case">(optional)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={manualTime}
                  onChange={(e) => setManualTime(e.target.value)}
                  data-testid="status-update-manual-time"
                  placeholder="e.g. 2.5"
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            <label className="text-xs uppercase tracking-wider font-bold text-zinc-400 block mb-2">
              Note{" "}
              {requiresNote ? (
                <span className="text-amber-400 normal-case">(required)</span>
              ) : (
                <span className="font-normal normal-case">(optional)</span>
              )}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="status-update-note"
              rows={3}
              placeholder={
                requiresNote
                  ? `Add a note for "${metaFor(status).label}"…`
                  : "Add a progress note…"
              }
              className={`w-full px-3 py-2 bg-zinc-900 border rounded-none text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                requiresNote && !note.trim() ? "border-amber-500" : "border-zinc-700"
              }`}
            />
            <button
              onClick={updateStatus}
              disabled={saving}
              data-testid="status-update-button"
              className="w-full bg-blue-600 text-white rounded-full font-bold tracking-tight hover:bg-blue-500 transition-colors py-2.5 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Update"}
            </button>
          </div>
          )}
        </div>
      </div>

      {lightbox !== null && imageAttachments[lightbox] && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-150"
          onClick={closeLightbox}
          data-testid="attachment-lightbox"
        >
          <button
            onClick={closeLightbox}
            data-testid="lightbox-close"
            className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={30} />
          </button>
          {imageAttachments.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + imageAttachments.length) % imageAttachments.length); }}
                data-testid="lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
                aria-label="Previous image"
              >
                <ChevronLeft size={40} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % imageAttachments.length); }}
                data-testid="lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
                aria-label="Next image"
              >
                <ChevronRight size={40} />
              </button>
            </>
          )}
          {/* Zoom controls */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/50 rounded-full px-2 py-1" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => adjustZoom(-0.5)} data-testid="lightbox-zoom-out" className="text-white/80 hover:text-white p-1.5 disabled:opacity-40" disabled={zoom <= 1} aria-label="Zoom out">
              <ZoomOut size={20} />
            </button>
            <span className="text-white/80 text-xs tabular-nums w-10 text-center" data-testid="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => adjustZoom(0.5)} data-testid="lightbox-zoom-in" className="text-white/80 hover:text-white p-1.5 disabled:opacity-40" disabled={zoom >= 5} aria-label="Zoom in">
              <ZoomIn size={20} />
            </button>
            <button
              onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
              data-testid="lightbox-zoom-reset"
              className="text-white/80 hover:text-white p-1.5 disabled:opacity-40 border-l border-white/20 ml-0.5 pl-2"
              disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              <Maximize size={18} />
            </button>
          </div>
          <div
            className="flex flex-col items-center gap-3 max-w-6xl max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="overflow-hidden max-w-full max-h-[82vh] rounded-lg shadow-2xl"
              onWheel={onImgWheel}
              onDoubleClick={() => (zoom > 1 ? adjustZoom(-4) : adjustZoom(1))}
              onPointerDown={onImgPointerDown}
              onPointerMove={onImgPointerMove}
              onPointerUp={onImgPointerUp}
              onPointerLeave={onImgPointerUp}
              style={{ cursor: zoom > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in" }}
            >
              <img
                src={thumbs[imageAttachments[lightbox].id]}
                alt={imageAttachments[lightbox].filename}
                data-testid="lightbox-image"
                draggable={false}
                className="max-w-full max-h-[82vh] object-contain select-none"
                style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transition: dragRef.current ? "none" : "transform 0.12s ease-out" }}
              />
            </div>
            <div className="text-white/90 text-sm font-medium tracking-tight">
              {imageAttachments[lightbox].filename}
              {imageAttachments.length > 1 && (
                <span className="text-white/50 ml-2">{lightbox + 1} / {imageAttachments.length}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {pdfView && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col p-4 sm:p-6 animate-in fade-in duration-150"
          data-testid="attachment-pdf-viewer"
        >
          <div className="flex items-center justify-between mb-3 text-white">
            <div className="flex items-center gap-2 text-sm font-medium truncate">
              <FileText size={18} className="text-red-400 shrink-0" />
              <span className="truncate">{pdfView.filename}</span>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <a
                href={pdfView.blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pdf-open-tab"
                className="text-white/70 hover:text-white text-xs font-bold flex items-center gap-1"
              >
                <Download size={15} /> Open
              </a>
              <button onClick={closePdf} data-testid="pdf-close" className="text-white/70 hover:text-white" aria-label="Close">
                <X size={26} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto rounded-lg bg-zinc-800/40 flex justify-center py-4" data-testid="pdf-scroll">
            <canvas ref={pdfCanvasRef} data-testid="pdf-canvas" className="shadow-2xl bg-white h-fit" />
          </div>
          {/* Page navigation */}
          <div className="mt-3 flex items-center justify-center gap-3">
            <button
              onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
              disabled={pdfPage <= 1}
              data-testid="pdf-prev-page"
              className="flex items-center gap-1 text-xs font-bold text-white/80 hover:text-white bg-black/50 rounded-full px-3 py-1.5 disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <span className="text-white/80 text-xs tabular-nums" data-testid="pdf-page-indicator">
              Page {pdfPage} / {pdfView.numPages}
            </span>
            <button
              onClick={() => setPdfPage((p) => Math.min(pdfView.numPages, p + 1))}
              disabled={pdfPage >= pdfView.numPages}
              data-testid="pdf-next-page"
              className="flex items-center gap-1 text-xs font-bold text-white/80 hover:text-white bg-black/50 rounded-full px-3 py-1.5 disabled:opacity-40"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
